import React, { useState, useEffect } from 'react';
import { getTodayYYYYMMDD, formatDateToDDMMYYYY } from '../../utils/dateUtils';
import './ExpenseManager.css';

const ExpenseManager = () => {
  const [form, setForm] = useState({
    headerName: '',
    givenTo: '',
    givenBy: '',
    amount: '',
    date: getTodayYYYYMMDD(),
  });

  const [transactions, setTransactions] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchHeader, setSearchHeader] = useState('');
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all transactions on mount
  useEffect(() => {
    fetchTransactions();
  }, []);

  // Apply filters whenever date filters or search header changes
  useEffect(() => {
    applyFilters();
  }, [transactions, filterDateFrom, filterDateTo, searchHeader]);

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5012'}/expense-manager`);
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.expenses || []);
      } else {
        console.error('Failed to fetch transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Filter by date range
    if (filterDateFrom && filterDateTo) {
      filtered = filtered.filter(t => {
        const txDate = new Date(t.date);
        const fromDate = new Date(filterDateFrom);
        const toDate = new Date(filterDateTo);
        return txDate >= fromDate && txDate <= toDate;
      });
    }

    // Filter by header name
    if (searchHeader) {
      filtered = filtered.filter(t => 
        t.headerName.toLowerCase().includes(searchHeader.toLowerCase())
      );
    }

    setFilteredTransactions(filtered);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handlePayment = async () => {
    // Validate all fields
    if (!form.headerName || !form.givenTo || !form.givenBy || !form.amount || !form.date) {
      alert('Please fill all required fields.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5012'}/expense-manager`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        const data = await response.json();
        alert('Expense saved successfully!');
        printSlip(form);
        setForm({
          headerName: '',
          givenTo: '',
          givenBy: '',
          amount: '',
          date: getTodayYYYYMMDD(),
        });
        fetchTransactions();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to save expense.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error occurred while saving expense.');
    }
  };

  const printSlip = (expenseData) => {
    const slipContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  .container { display: flex; }
  .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 15px; }
  .details { margin: 10px 0; }
  .line { display: flex; justify-content: space-between; margin: 5px 0; }
  .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  .table td { padding: 5px; }
  .table .right { text-align: right; font-weight: bold; }
  .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
  .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
</style>

<div class="container">
  <!-- Left Receipt -->
  <div class="left-receipt">
    <div class="header">M/S SHREE MAHAKAL FINANCE AND LEASING (R)</div>
    <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="details">
      <div class="line"><strong>Date:</strong> ${expenseData.date ? formatDateToDDMMYYYY(expenseData.date) : '__________'}</div>
    </div>
    <div class="details">
      <div class="line"><strong>Paid to Sri:</strong> ${expenseData.givenTo}</div>
      <div class="line"><strong>Amount Rs:</strong> ₹ ${expenseData.amount}</div>
    </div>
    <table class="table">
      <tr>
        <td>${expenseData.headerName}</td>
        <td class="right">${Number(expenseData.amount).toFixed(2)}</td>
      </tr>
    </table>
    <div class="total">
      TOTAL: ₹ ${Number(expenseData.amount).toFixed(2)}
    </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>

  <!-- Right Receipt -->
  <div class="right-receipt">
    <div class="header">M/S SHREE MAHAKAL FINANCE AND LEASING (R)</div>
    <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="details">
      <div class="line"><strong>Date:</strong> ${expenseData.date ? formatDateToDDMMYYYY(expenseData.date) : '__________'}</div>
    </div>
    <div class="details">
      <div class="line"><strong>Paid to Sri:</strong> ${expenseData.givenTo}</div>
      <div class="line"><strong>Amount Rs:</strong> ₹ ${expenseData.amount}</div>
    </div>
    <table class="table">
      <tr>
        <td>${expenseData.headerName}</td>
        <td class="right">${Number(expenseData.amount).toFixed(2)}</td>
      </tr>
    </table>
    <div class="total">
      TOTAL: ₹ ${Number(expenseData.amount).toFixed(2)}
    </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>
</div>
`;

    const printWindow = window.open('', '', 'height=800,width=1200');
    printWindow.document.write('<html><head><title>Expense Slip</title></head><body>');
    printWindow.document.write(slipContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const printTransactionHistory = () => {
    if (filteredTransactions.length === 0) {
      alert('No transactions to print.');
      return;
    }

    const total = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    const dateRange = filterDateFrom && filterDateTo 
      ? `From: ${formatDateToDDMMYYYY(filterDateFrom)} - To: ${formatDateToDDMMYYYY(filterDateTo)}`
      : 'All Transactions';

    const historyContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 20px; padding: 20px; }
  .header { text-align: center; font-weight: bold; font-size: 1.5em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 1em; margin-bottom: 15px; }
  .date-range { text-align: center; margin-bottom: 20px; font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th, td { border: 1px solid #000; padding: 8px; text-align: left; }
  th { background-color: #f0f0f0; font-weight: bold; }
  .right { text-align: right; }
  .total-row { background-color: #f0f0f0; font-weight: bold; }
  .footer { text-align: center; margin-top: 30px; font-size: 0.9em; }
</style>

<div class="header">M/S SHREE MAHAKAL FINANCE AND LEASING (R)</div>
<div class="sub-header">Khajuri, Aland , Kalaburgi</div>
<div class="date-range">${dateRange}</div>

<table>
  <thead>
    <tr>
      <th>Date</th>
      <th>Header Name</th>
      <th>Given To</th>
      <th>Given By</th>
      <th class="right">Amount (₹)</th>
    </tr>
  </thead>
  <tbody>
    ${filteredTransactions.map(t => `
      <tr>
        <td>${formatDateToDDMMYYYY(t.date)}</td>
        <td>${t.headerName}</td>
        <td>${t.givenTo}</td>
        <td>${t.givenBy}</td>
        <td class="right">${Number(t.amount).toFixed(2)}</td>
      </tr>
    `).join('')}
    <tr class="total-row">
      <td colspan="4" style="text-align: right;"><strong>TOTAL:</strong></td>
      <td class="right"><strong>₹ ${total.toFixed(2)}</strong></td>
    </tr>
  </tbody>
</table>

<div class="footer">
  Printed on: ${formatDateToDDMMYYYY(getTodayYYYYMMDD())}
</div>
`;

    const printWindow = window.open('', '', 'height=800,width=1200');
    printWindow.document.write('<html><head><title>Expense Transaction History</title></head><body>');
    printWindow.document.write(historyContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const clearFilters = () => {
    setFilterDateFrom('');
    setFilterDateTo('');
    setSearchHeader('');
  };

  return (
    <div className="expense-manager-container">
      <h2>Expense Manager</h2>

      {/* Expense Form */}
      <div className="expense-form-box">
        <h3>Add Expense</h3>
        <form className="expense-form">
          <label>
            Header Name: <span className="required">*</span>
            <select
              name="headerName"
              value={form.headerName}
              onChange={handleChange}
              className="form-input"
              required
            >
              <option value="">Select Header Name</option>
              <option value="Office Expenditure A/C">Office Expenditure A/C</option>
              <option value="POOJA A/C">POOJA A/C</option>
              <option value="PRITING & STATIONRY A/C">PRITING & STATIONRY A/C</option>
              <option value="FURNITURE A/C">FURNITURE A/C</option>
              <option value="TELE PHONE A/C">TELE PHONE A/C</option>
              <option value="ROOM RENT A/C">ROOM RENT A/C</option>
              <option value="BULDING DEP A/C">BULDING DEP A/C</option>
              <option value="MISC A/C">MISC A/C</option>
              <option value="LEGAL ADVISOR & ADIT F">LEGAL ADVISOR & ADIT F</option>
              <option value="MEETING EXP A/C">MEETING EXP A/C</option>
              <option value="SECURITY DEP A/C">SECURITY DEP A/C</option>
              <option value="NSC">NSC</option>
              <option value=" LICENCE FEES A/C">LICENCE FEES A/C</option>
              <option value="COMPUTER A/C">COMPUTER A/C</option>
              <option value="STAMP DUTY A/C">STAMP DUTY A/C</option>
              <option value="ELECTRICAL EXP A/C">ELECTRICAL EXP A/C</option>
              <option value="NEWS PAPER & CABLE A/C">NEWS PAPER & CABLE A/C</option>
              <option value="Interest On Capital">Interest On Capital</option>
              <option value="M.G.PARTNER SALARI A/C">M.G.PARTNER SALARI A/C</option>
              <option value="PARTNER SALARIES A/C">PARTNER SALARIES A/C</option>
              <option value="STAFF SALARY A/C">STAFF SALARY A/C</option>
              <option value="Intrest Paid On Deposit A/c">Intrest Paid On Deposit A/c</option>
              <option value="NETT LOSS A/C">NETT LOSS A/C</option>
              <option value="Professional Tax">Professional Tax</option>
              <option value="Income Tax">Income Tax</option>
              <option value="BANK CHARGES">BANK CHARGES</option>
              <option value="Document Charges A/C">Document Charges A/C</option>
              <option value="CC TV">CC TV</option>
              <option value="ACCOUNT SOFTWARE">ACCOUNT SOFTWARE</option>
              <option value="MD SALARY">MD SALARY</option>
              <option value="BATTA">BATTA</option>
              <option value="SOFTWARE A/C">SOFTWARE A/C</option>
              <option value="GOLD PURCHASE">GOLD PURCHASE</option>
              <option value="PARTNERS INCENTIVE">PARTNERS INCENTIVE</option>
              <option value="Tea Expenditure">Tea Expenditure</option>
            </select>
          </label>
          <label>
            Given To: <span className="required">*</span>
            <input
              type="text"
              name="givenTo"
              value={form.givenTo}
              onChange={handleChange}
              className="form-input"
              required
            />
          </label>
          <label>
            Given By: <span className="required">*</span>
            <input
              type="text"
              name="givenBy"
              value={form.givenBy}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter name"
              required
            />
          </label>
          <label>
            Amount: <span className="required">*</span>
            <input
              type="number"
              name="amount"
              value={form.amount}
              onChange={handleChange}
              className="form-input"
              required
            />
          </label>
          <label>
            Date: <span className="required">*</span>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              className="form-input"
              required
            />
          </label>
        </form>
        <button onClick={handlePayment} className="submit-button">
          Make Payment
        </button>
      </div>

      {/* Transaction History */}
      <div className="transaction-history-section">
        <h3>Transaction History</h3>

        {/* Filters */}
        <div className="filters-container">
          <div className="filter-group">
            <label>From Date:</label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>To Date:</label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>Search by Header Name:</label>
            <input
              type="text"
              value={searchHeader}
              onChange={(e) => setSearchHeader(e.target.value)}
              placeholder="Enter header name"
              className="filter-input"
            />
          </div>
          <div className="filter-buttons">
            <button onClick={clearFilters} className="clear-button">
              Clear Filters
            </button>
            <button onClick={printTransactionHistory} className="print-button">
              Print History
            </button>
          </div>
        </div>

        {/* Transactions Table */}
        {isLoading ? (
          <div className="loading-message">Loading transactions...</div>
        ) : filteredTransactions.length === 0 ? (
          <div className="no-data-message">No transactions found</div>
        ) : (
          <>
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Header Name</th>
                  <th>Given To</th>
                  <th>Given By</th>
                  <th className="right">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{formatDateToDDMMYYYY(transaction.date)}</td>
                    <td>{transaction.headerName}</td>
                    <td>{transaction.givenTo}</td>
                    <td>{transaction.givenBy}</td>
                    <td className="right">{Number(transaction.amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="4" className="total-label">TOTAL:</td>
                  <td className="right total-amount">
                    ₹ {filteredTransactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default ExpenseManager;

